import { useState, useEffect } from "react";
import { useRouter } from "next/router";

import {
  Button,
  CloseSVG,
  ChevronLeftSVG,
  FilesSVG,
  LoadingSpinner,
} from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";

import { LessonSparkAttachment } from "@/bundles/types/LessonSparkAttachment";
import { useAttachmentsContext } from "@/bundles/providers/useAttachmentsContext";

import AttachmentItem from "../AttachmentItem";
import {
  panelContainerCss,
  iconCss,
  headerCss,
  sectionCss,
  itemCss,
  itemIconCss,
  headerTitleCss,
} from "./AttachmentPanel.styles";

const CONTENT_TYPE_TITLE_MAPPING: Record<string, string> = {
  LESSON_SPARK: "Lesson Spark",
};

const ButtonWithTrackEvent = withTrackEvent(Button);

const AttachmentPanel = () => {
  const { attachmentData, isAttachmentsFetching, setIsResourcePanelOpen } =
    useAttachmentsContext();
  const router = useRouter();
  const { attachment: attachmentIdFromUrl } = router.query as {
    attachment?: string;
  };

  const [selectedResource, setSelectedResource] =
    useState<LessonSparkAttachment | null>(null);

  // Sync selected resource with the URL attachment param.
  // Handles deep-link selection on load and browser back/forward navigation.
  useEffect(() => {
    if (attachmentIdFromUrl && attachmentData && !isAttachmentsFetching) {
      const attachment = (attachmentData as LessonSparkAttachment[]).find(
        (item) => item.id === attachmentIdFromUrl,
      );
      if (attachment) {
        setSelectedResource(attachment);
      }
    } else if (!attachmentIdFromUrl) {
      setSelectedResource(null);
    }
  }, [attachmentIdFromUrl, attachmentData, isAttachmentsFetching]);

  if (isAttachmentsFetching) {
    return <LoadingSpinner />;
  }

  if (!attachmentData || !(attachmentData as LessonSparkAttachment[])?.length)
    return null;

  const onClickResourceItem = (id: string) => {
    const selectedItem = (attachmentData as LessonSparkAttachment[]).find(
      (attachment) => attachment.id === id,
    ) as LessonSparkAttachment;
    setSelectedResource(selectedItem);

    // Sync URL
    router.replace(
      {
        pathname: router.pathname,
        query: { ...router.query, attachment: id },
      },
      undefined,
      { shallow: true },
    );
  };

  return (
    <div css={panelContainerCss}>
      <div css={headerCss}>
        {/* Resource panel header */}
        {selectedResource ? (
          <ButtonWithTrackEvent
            ariaProps={{ "aria-label": "Go back to resource list" }}
            dataProps={{
              "data-qa-selector":
                "go-back-to-resource-list-bundle-page-resource-panel",
            }}
            flavor={Button.flavors.incognito}
            onClick={() => {
              setSelectedResource(null);
              // Remove attachment param from URL
              const { attachment, ...restQuery } = router.query;
              router.replace(
                {
                  pathname: router.pathname,
                  query: restQuery,
                },
                undefined,
                { shallow: true },
              );
            }}
            actionName={actionNames.interact}
            objectType={objectTypes.content}
            streamProperties={{
              description: "Go back to resource list on bundle page",
            }}
          >
            <ChevronLeftSVG css={iconCss} />
          </ButtonWithTrackEvent>
        ) : null}

        <h2 css={headerTitleCss}>
          {selectedResource
            ? CONTENT_TYPE_TITLE_MAPPING?.[selectedResource.content_type]
            : "Resources"}
        </h2>

        <ButtonWithTrackEvent
          ariaProps={{ "aria-label": "Close resource panel" }}
          dataProps={{ "data-qa-selector": "close-bundle-page-resource-panel" }}
          flavor={Button.flavors.incognito}
          onClick={() => {
            setIsResourcePanelOpen(false);
            const { attachment, ...restQuery } = router.query;
            router.replace(
              {
                pathname: router.pathname,
                query: restQuery,
              },
              undefined,
              { shallow: true },
            );
          }}
          actionName={actionNames.interact}
          objectType={objectTypes.content}
          streamProperties={{
            description: "Close bundles page resource panel",
          }}
        >
          <CloseSVG css={iconCss} />
        </ButtonWithTrackEvent>
      </div>

      {/* Resource list view */}
      {!selectedResource ? (
        <>
          {(attachmentData as LessonSparkAttachment[])?.map(
            (attachment: LessonSparkAttachment, index) => {
              return (
                <div key={index} css={sectionCss}>
                  <ButtonWithTrackEvent
                    flavor={Button.flavors.incognito}
                    __cssFor={{
                      root: itemCss,
                    }}
                    onClick={() => onClickResourceItem(attachment.id)}
                    actionName={actionNames.interact}
                    objectType={objectTypes.content}
                    streamProperties={{
                      description: "Select a bundle resource from the panel",
                    }}
                  >
                    <FilesSVG css={itemIconCss} />
                    {attachment?.title || ""}
                  </ButtonWithTrackEvent>
                </div>
              );
            },
          )}
        </>
      ) : null}

      {/* Single resource item view */}
      {selectedResource ? (
        <AttachmentItem selectedResource={selectedResource} />
      ) : null}
    </div>
  );
};

export default AttachmentPanel;
